import { useState, useEffect, useRef } from "react";
import { PlatformLayout } from "./platform-layout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, bootstrapCsrfToken, readCsrfCookie } from "@/lib/queryClient";
import {
  Search, Loader2, Trash2, Download, Star, MapPin, Phone, Globe,
  ExternalLink, AlertTriangle, CheckCircle2, X, RefreshCw, Coins,
  Plus, Sparkles, Mail, Save, Play, Linkedin, ChevronDown, Building2,
  Check, HelpCircle, Ban, Instagram, Link2, FileText, Store, Users,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

interface PlatformSandboxItem {
  id: string;
  platformUserId: string;
  searchId: string | null;
  googlePlaceId: string;
  businessName: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  phone: string | null;
  website: string | null;
  googleRating: number | null;
  googleRatingsCount: number | null;
  googleMapsUrl: string | null;
  photoReference: string | null;
  latitude: number | null;
  longitude: number | null;
  distanceMiles: number | null;
  quadrant: string | null;
  isDuplicate: boolean;
  ignored: boolean;
  isAiGenerated: boolean;
  verificationStatus: string | null;
  aiFitScore: number | null;
  aiContactName: string | null;
  aiContactRole: string | null;
  aiContactEmail: string | null;
  aiVenueType: string | null;
  aiVenueCapacity: string | null;
  aiNotes: string | null;
  aiSourceLabel: string | null;
  aiConfidenceJson: {
    emailStatus?: string | null;
    emailSource?: string | null;
    emailConfidence?: "verified" | "catchall" | "low" | null;
    leadType?: "performer" | "venue" | null;
    channels?: Array<{ kind: "phone" | "instagram" | "contact_form" | "links" | "marketplace"; value: string; source?: string }> | null;
    reason?: string | null;
    // Deep Analyst (Opus premium layer)
    score?: number | null;
    scoreReason?: string | null;
    audit?: { verdict?: "pass" | "flag" | "mismatch" | string; confidence?: number | null; reason?: string | null } | null;
    bookerEscalated?: boolean | null;
    bookerReason?: string | null;
    bookerContacts?: Array<{ name?: string | null; title?: string | null; email?: string | null; phone?: string | null; isPrimary?: boolean }> | null;
    additionalContacts?: Array<{ name?: string | null; title?: string | null; email?: string | null; phone?: string | null }> | null;
    [k: string]: any;
  } | null;
  createdAt: string;
}

interface PlatformSearch {
  id: string;
  keyword: string;
  location: string;
  radiusMiles: number;
  status: "PENDING" | "RUNNING" | "COMPLETE" | "FAILED";
  resultCount: number;
  totalSubSearches: number;
  completedSubSearches: number;
  errorMessage: string | null;
  createdAt: string;
  source?: "ai" | "grid" | null;
}

interface SandboxData {
  searches: PlatformSearch[];
  items: PlatformSandboxItem[];
  settings: { quadrantTagsEnabled: boolean };
}

interface SavedSearch {
  id: string;
  name: string;
  brief: string;
  scope: any;
  filters: any;
  recurring: boolean;
}

interface CreditsBalance {
  grantBalance: number;
  purchasedBalance: number;
  totalBalance: number;
  pricePerCreditCents: number;
  packs: Array<{ priceCents: number; credits: number; label: string }>;
}

interface DuplicateMatch {
  sandboxItemId: string;
  businessName: string;
  phone: string | null;
  matchedFields: string[];
  existingRecord: { name: string; type: string };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PlatformLeadGenPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Grid search form
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("25");

  // Modal state
  const [importOpen, setImportOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [awardOpen, setAwardOpen] = useState(false);
  const [awardAmount, setAwardAmount] = useState("500");

  // Import state
  const [skipIds, setSkipIds] = useState<Set<string>>(new Set());
  type ImportStep = "form" | "checking" | "review";
  const [importStep, setImportStep] = useState<ImportStep>("form");
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([]);
  const [dupSkipIds, setDupSkipIds] = useState<Set<string>>(new Set());

  // Polling
  const [isPolling, setIsPolling] = useState(false);

  // AI Search state
  const [activeTab, setActiveTab] = useState<"grid" | "ai">("grid");
  const [aiBrief, setAiBrief] = useState("");
  const [aiScopeKind, setAiScopeKind] = useState<"city" | "state" | "region" | "national" | "cities" | "custom">("city");
  const [aiScopeValue, setAiScopeValue] = useState("");
  const [aiVenueTypesText, setAiVenueTypesText] = useState("");
  const [aiCapacityMin, setAiCapacityMin] = useState("");
  const [aiCapacityMax, setAiCapacityMax] = useState("");
  const [aiGenres, setAiGenres] = useState("");
  const [aiExclude, setAiExclude] = useState("");
  const [aiMinFitScore, setAiMinFitScore] = useState("3");
  const [aiResultCap, setAiResultCap] = useState("");
  const [aiSpendCap, setAiSpendCap] = useState("");
  const [aiModel, setAiModel] = useState<"claude-haiku-4-5" | "claude-sonnet-4-5">("claude-haiku-4-5");
  const [aiSaveAs, setAiSaveAs] = useState("");
  const [aiRecurring, setAiRecurring] = useState(false);
  const [aiTargetContact, setAiTargetContact] = useState("");
  const [showRefinements, setShowRefinements] = useState(false);
  // Deep Analyst (premium): Opus Market Cartography + 0-100 scoring + booker-hunt
  // escalation + final logic audit. Only runs on the Analyst (Sonnet) path.
  const [deepAnalyst, setDeepAnalyst] = useState(false);
  const [scoreThreshold, setScoreThreshold] = useState(65);

  // Analyst SSE state
  const [analystRunning, setAnalystRunning] = useState(false);
  const [analystPhase, setAnalystPhase] = useState(0);
  const [analystPhaseLabel, setAnalystPhaseLabel] = useState("");
  const [analystPhaseDetail, setAnalystPhaseDetail] = useState("");
  const [analystDone, setAnalystDone] = useState<any>(null);
  const [showAnalystLocation, setShowAnalystLocation] = useState(false);
  const [analystLocationInput, setAnalystLocationInput] = useState("");

  // Per-row busy state
  const [rowBusy, setRowBusy] = useState<Record<string, "enrich" | "email" | "apollo" | null>>({});

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: sandboxData, isLoading: sandboxLoading } = useQuery<SandboxData>({
    queryKey: ["/api/platform/lead-gen/sandbox"],
    refetchInterval: isPolling ? 3000 : false,
  });

  const { data: credits } = useQuery<CreditsBalance>({
    queryKey: ["/api/platform/lead-gen/credits/balance"],
  });

  const { data: savedSearches } = useQuery<SavedSearch[]>({
    queryKey: ["/api/platform/lead-gen/ai/saved-searches"],
  });

  const searches = sandboxData?.searches || [];
  const items = sandboxData?.items || [];

  // Poll while any search is running
  useEffect(() => {
    const hasRunning = searches.some((s) => s.status === "PENDING" || s.status === "RUNNING");
    setIsPolling(hasRunning);
  }, [searches]);

  // Derived
  const activeItems = items.filter((i) => !i.ignored);
  const totalToImport = activeItems.filter((i) => !skipIds.has(i.id)).length;
  const reviewImportCount = totalToImport - dupSkipIds.size;

  // ── Mutations ─────────────────────────────────────────────────────────────

  const startSearchMutation = useMutation({
    mutationFn: async (body: { keyword: string; location: string; radiusMiles: number }) => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/search", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
      setKeyword("");
      setLocation("");
    },
    onError: (err: any) => toast({ title: "Search failed", description: err.message, variant: "destructive" }),
  });

  const aiSearchMutation = useMutation({
    mutationFn: async (body: any) => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/ai/search", body);
      if (!res.ok) {
        const data = await res.json();
        const err: any = new Error(data.message || "AI search failed");
        err.code = data.code;
        throw err;
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
      toast({
        title: "AI Search complete",
        description: `${data.inserted} prospect${data.inserted !== 1 ? "s" : ""} added to sandbox. ${data.creditsCharged} credit${data.creditsCharged !== 1 ? "s" : ""} charged.`,
      });
    },
    onError: (err: any) => {
      if (err.code === "INSUFFICIENT_CREDITS") {
        toast({ title: "Insufficient credits", description: err.message, variant: "destructive" });
      } else {
        toast({ title: "AI search failed", description: err.message, variant: "destructive" });
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/platform/lead-gen/sandbox/${id}`, undefined);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] }),
    onError: (err: any) => toast({ title: "Remove failed", description: err.message, variant: "destructive" }),
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/platform/lead-gen/sandbox", undefined);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
      setClearOpen(false);
      toast({ title: "Sandbox cleared" });
    },
    onError: (err: any) => toast({ title: "Clear failed", description: err.message, variant: "destructive" }),
  });

  const bulkEnrichMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/sandbox/bulk-find-email", {});
      return res.json();
    },
    onSuccess: (data: { processed: number; found: number; creditsCharged: number; message?: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
      toast({
        title: data.processed === 0 ? "Nothing to re-enrich" : "Bulk re-enrich complete",
        description: data.processed === 0
          ? (data.message || "Every staged row already has a contact email.")
          : `${data.found} of ${data.processed} rows got a verified email · ${data.creditsCharged} credits used`,
      });
    },
    onError: (err: any) => {
      if (err?.code === "INSUFFICIENT_CREDITS") toast({ title: "Insufficient credits", description: err.message, variant: "destructive" });
      else toast({ title: "Bulk re-enrich failed", description: err.message, variant: "destructive" });
    },
  });

  const checkDupsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/check-duplicates", {});
      return res.json();
    },
    onSuccess: (data: { duplicates: DuplicateMatch[]; totalItems: number }) => {
      setDuplicateMatches(data.duplicates);
      setDupSkipIds(new Set(data.duplicates.map((d) => d.sandboxItemId)));
      setImportStep("review");
    },
    onError: (err: any) => {
      toast({ title: "Duplicate check failed", description: err.message, variant: "destructive" });
      setImportStep("form");
    },
  });

  const importMutation = useMutation({
    mutationFn: async (body: { skipIds: string[] }) => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/import", body);
      if (!res.ok) {
        const data = await res.json();
        const err: any = new Error(data.message || "Import failed");
        err.code = data.code;
        throw err;
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/platform/prospects"] });
      setImportOpen(false);
      setImportStep("form");
      setSkipIds(new Set());
      setDupSkipIds(new Set());
      setDuplicateMatches([]);
      toast({
        title: "Import complete",
        description: `${data.imported} prospect${data.imported !== 1 ? "s" : ""} imported. ${data.creditsCharged} credit${data.creditsCharged !== 1 ? "s" : ""} charged.`,
      });
    },
    onError: (err: any) => {
      if (err.code === "INSUFFICIENT_CREDITS") {
        toast({ title: "Insufficient credits", description: err.message, variant: "destructive" });
      } else {
        toast({ title: "Import failed", description: err.message, variant: "destructive" });
      }
    },
  });

  const saveSearchMutation = useMutation({
    mutationFn: async (body: any) => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/ai/saved-searches", body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/ai/saved-searches"] });
      setAiSaveAs("");
      toast({ title: "Search saved" });
    },
    onError: (err: any) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const deleteSavedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/platform/lead-gen/ai/saved-searches/${id}`, undefined);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/ai/saved-searches"] }),
  });

  const runSavedMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/platform/lead-gen/ai/saved-searches/${id}/run`, {});
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.body) aiSearchMutation.mutate(data.body);
    },
  });

  const awardCreditsMutation = useMutation({
    mutationFn: async (amount: number) => {
      const res = await apiRequest("POST", "/api/platform/lead-gen/credits/award", { amount, notes: "Manual self-award" });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
      setAwardOpen(false);
      toast({ title: "Credits awarded" });
    },
    onError: (err: any) => toast({ title: "Award failed", description: err.message, variant: "destructive" }),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSearch() {
    if (!keyword.trim() || !location.trim()) return;
    startSearchMutation.mutate({ keyword: keyword.trim(), location: location.trim(), radiusMiles: parseInt(radiusMiles, 10) || 25 });
  }

  function buildAiPayload() {
    const scope: any = { kind: aiScopeKind };
    if (aiScopeKind !== "national") scope.value = aiScopeValue.trim();
    const filters: any = { modelKey: aiModel };
    const resultCapNum = parseInt(aiResultCap, 10);
    if (!isNaN(resultCapNum) && resultCapNum > 0) filters.resultCap = resultCapNum;
    const spendCapNum = parseFloat(aiSpendCap);
    if (!isNaN(spendCapNum) && spendCapNum > 0) filters.spendCapUsd = spendCapNum;
    if (aiMinFitScore) filters.minFitScore = parseInt(aiMinFitScore, 10);
    if (aiVenueTypesText.trim()) filters.venueTypes = aiVenueTypesText.split(",").map((s) => s.trim()).filter(Boolean);
    if (aiCapacityMin) filters.capacityMin = parseInt(aiCapacityMin, 10);
    if (aiCapacityMax) filters.capacityMax = parseInt(aiCapacityMax, 10);
    if (aiGenres.trim()) filters.genres = aiGenres.split(",").map((s) => s.trim()).filter(Boolean);
    if (aiExclude.trim()) filters.exclude = aiExclude.split(",").map((s) => s.trim()).filter(Boolean);
    if (aiTargetContact.trim()) filters.targetContact = aiTargetContact.trim();
    return { brief: aiBrief.trim(), scope, filters };
  }

  function handleAiSearch() {
    if (!aiBrief.trim()) return;
    if (aiModel === "claude-sonnet-4-5") {
      const hasSpecificLocation = aiScopeKind === "city" || aiScopeKind === "cities";
      if (hasSpecificLocation) {
        runAnalyst(aiScopeValue.trim() || undefined);
      } else {
        const prefill = (aiScopeKind === "state" || aiScopeKind === "region") ? aiScopeValue : "";
        setAnalystLocationInput(prefill);
        setShowAnalystLocation(true);
      }
    } else {
      aiSearchMutation.mutate(buildAiPayload());
    }
  }

  async function runAnalyst(locationOverride?: string) {
    setAnalystRunning(true);
    setAnalystPhase(1);
    setAnalystPhaseLabel("🗺️ Scouting the landscape...");
    setAnalystPhaseDetail("");
    setAnalystDone(null);
    try {
      const csrfToken = readCsrfCookie() ?? (await bootstrapCsrfToken());
      const res = await fetch("/api/platform/lead-gen/ai/analyst-search", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ ...buildAiPayload(), locationOverride: locationOverride || null, deepAnalyst, scoreThreshold }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message || "Analyst search failed");
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          const dataLine = chunk.split("\n").find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine.slice(6));
            if (data.phase !== undefined) {
              setAnalystPhase(data.phase);
              setAnalystPhaseLabel(data.label || "");
              setAnalystPhaseDetail(data.detail || "");
            }
            if (data.error) throw new Error(data.error);
            if (data.done) {
              setAnalystDone(data);
              toast({ title: "Analyst complete!", description: `${data.inserted} leads added · ${data.creditsCharged} credits used` });
              queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
              queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
            }
          } catch (parseErr: any) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }
    } catch (err: any) {
      toast({ title: "Analyst search failed", description: err.message, variant: "destructive" });
    } finally {
      setAnalystRunning(false);
    }
  }

  function handleSaveSearch() {
    if (!aiSaveAs.trim() || !aiBrief.trim()) return;
    const payload = buildAiPayload();
    saveSearchMutation.mutate({ name: aiSaveAs.trim(), brief: payload.brief, scope: payload.scope, filters: payload.filters, recurring: aiRecurring });
  }

  function handleOpenImport() {
    setImportStep("form");
    setSkipIds(new Set());
    setDupSkipIds(new Set());
    setDuplicateMatches([]);
    setImportOpen(true);
  }

  async function handleCheckDuplicates() {
    setImportStep("checking");
    checkDupsMutation.mutate();
  }

  function handleReviewImport() {
    const allSkip = [...skipIds, ...dupSkipIds];
    importMutation.mutate({ skipIds: allSkip });
  }

  function toggleDupSkip(id: string) {
    setDupSkipIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSkip(id: string) {
    setSkipIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleEnrich(item: PlatformSandboxItem) {
    setRowBusy((prev) => ({ ...prev, [item.id]: "enrich" }));
    try {
      const res = await apiRequest("POST", `/api/platform/lead-gen/sandbox/${item.id}/enrich`, {});
      if (!res.ok) {
        const d = await res.json();
        toast({ title: "Enrich failed", description: d.message, variant: "destructive" });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
        toast({ title: "Enriched", description: `1 credit charged.` });
      }
    } catch (err: any) {
      toast({ title: "Enrich failed", description: err.message, variant: "destructive" });
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: null }));
    }
  }

  async function handleFindEmail(item: PlatformSandboxItem) {
    setRowBusy((prev) => ({ ...prev, [item.id]: "email" }));
    try {
      const res = await apiRequest("POST", `/api/platform/lead-gen/sandbox/${item.id}/find-email`, {});
      const data = await res.json();
      if (!res.ok) {
        toast({ title: "Email finder failed", description: data.message, variant: "destructive" });
      } else if (!data.found) {
        toast({ title: "No email found", description: "Credit refunded." });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
        toast({ title: "Email found", description: `${data.found.email} · 1 credit charged.` });
      }
    } catch (err: any) {
      toast({ title: "Email finder failed", description: err.message, variant: "destructive" });
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: null }));
    }
  }

  async function handleApollo(item: PlatformSandboxItem) {
    setRowBusy((prev) => ({ ...prev, [item.id]: "apollo" }));
    try {
      const res = await apiRequest("POST", `/api/platform/lead-gen/sandbox/${item.id}/apollo`, {});
      const data = await res.json();
      if (!res.ok) {
        toast({ title: "Apollo failed", description: data.message, variant: "destructive" });
      } else if (!data.person) {
        toast({ title: "No contact found", description: "Credit refunded." });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/sandbox"] });
        queryClient.invalidateQueries({ queryKey: ["/api/platform/lead-gen/credits/balance"] });
        toast({ title: "Apollo lookup complete", description: `${data.person.name || "Contact"} found. 1 credit charged.` });
      }
    } catch (err: any) {
      toast({ title: "Apollo failed", description: err.message, variant: "destructive" });
    } finally {
      setRowBusy((prev) => ({ ...prev, [item.id]: null }));
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <PlatformLayout>
    <TooltipProvider>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                Platform Lead Gen
              </h1>
              {credits && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/40 cursor-default" data-testid="platform-lead-credits-panel">
                      <Coins className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-medium" data-testid="text-platform-credit-balance">
                        {credits.totalBalance.toLocaleString()} credits
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <div className="text-xs space-y-1">
                      <div>Grant pool: <span className="font-medium">{credits.grantBalance.toLocaleString()}</span></div>
                      <div>Purchased: <span className="font-medium">{credits.purchasedBalance.toLocaleString()}</span></div>
                      <div className="text-muted-foreground pt-1 border-t">1 credit = 1 imported prospect</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
              <Button size="sm" variant="outline" onClick={() => setAwardOpen(true)} data-testid="button-award-credits">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Credits
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Search Google Places to build a prospect list. Review, qualify, and import to Platform Prospects when ready.
              {credits && credits.totalBalance === 0 && (
                <span className="ml-2 text-amber-600 dark:text-amber-500 font-medium">
                  Credits exhausted — add more to continue importing.
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeItems.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearOpen(true)}
                  data-testid="button-clear-sandbox"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Clear sandbox
                </Button>
                <Button
                  size="sm"
                  onClick={handleOpenImport}
                  disabled={activeItems.length === 0}
                  data-testid="button-import-prospects"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Import to Prospects
                  <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5">
                    {activeItems.length}
                  </Badge>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Analyst location dialog */}
        <Dialog open={showAnalystLocation} onOpenChange={setShowAnalystLocation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Where should we search locally?</DialogTitle>
              <DialogDescription>
                Analyst runs targeted local searches in addition to AI discovery. Enter a city, state, or region — or skip to use AI discovery only.
              </DialogDescription>
            </DialogHeader>
            <input
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder='e.g. "Minneapolis, MN" or "Minnesota" or "Dallas, TX"'
              value={analystLocationInput}
              onChange={(e) => setAnalystLocationInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setShowAnalystLocation(false); runAnalyst(analystLocationInput.trim() || undefined); } }}
              autoFocus
              data-testid="input-analyst-location"
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => { setShowAnalystLocation(false); runAnalyst(undefined); }} data-testid="button-analyst-skip-location">
                Skip local search
              </Button>
              <Button onClick={() => { setShowAnalystLocation(false); runAnalyst(analystLocationInput.trim() || undefined); }} data-testid="button-analyst-confirm-location">
                Run Analyst
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Award Credits Dialog */}
        <Dialog open={awardOpen} onOpenChange={setAwardOpen}>
          <DialogContent data-testid="dialog-award-credits">
            <DialogHeader>
              <DialogTitle>Add Platform Credits</DialogTitle>
              <DialogDescription>
                Award credits to your platform lead gen pool. These come from the platform's own quota.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <Label>Credits to add</Label>
              <Input
                type="number"
                min="1"
                max="10000"
                value={awardAmount}
                onChange={(e) => setAwardAmount(e.target.value)}
                data-testid="input-award-amount"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAwardOpen(false)}>Cancel</Button>
              <Button
                onClick={() => awardCreditsMutation.mutate(parseInt(awardAmount, 10) || 0)}
                disabled={awardCreditsMutation.isPending || !parseInt(awardAmount, 10)}
                data-testid="button-confirm-award"
              >
                {awardCreditsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Add {parseInt(awardAmount, 10) || 0} credits
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Main content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6 space-y-6">

            {/* Search tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "grid" | "ai")}>
              <TabsList className="mb-3" data-testid="tabs-leadgen-mode">
                <TabsTrigger value="grid" data-testid="tab-grid-search">
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Grid Search
                </TabsTrigger>
                <TabsTrigger value="ai" data-testid="tab-ai-search">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI Search
                </TabsTrigger>
              </TabsList>

              {/* Grid Search */}
              <TabsContent value="grid" className="mt-0">
                <div className="bg-card border rounded-lg p-5">
                  <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    New Search
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div className="md:col-span-1 space-y-1">
                      <Label htmlFor="lg-keyword" className="text-xs">Business Type / Keyword</Label>
                      <Input
                        id="lg-keyword"
                        placeholder='e.g. "country club"'
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        data-testid="input-leadgen-keyword"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label htmlFor="lg-location" className="text-xs">Location</Label>
                      <Input
                        id="lg-location"
                        placeholder='e.g. "Denver, CO" or "Milwaukee, WI"'
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        data-testid="input-leadgen-location"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="lg-radius" className="text-xs">Radius (miles)</Label>
                      <div className="flex gap-2">
                        <Select value={radiusMiles} onValueChange={setRadiusMiles}>
                          <SelectTrigger id="lg-radius" data-testid="select-leadgen-radius">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["5", "10", "15", "25", "35", "50", "75", "100"].map((r) => (
                              <SelectItem key={r} value={r}>{r} mi</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={handleSearch}
                          disabled={!keyword.trim() || !location.trim() || startSearchMutation.isPending}
                          data-testid="button-leadgen-search"
                          className="shrink-0"
                        >
                          {startSearchMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                          <span className="ml-1.5 hidden sm:inline">Search</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Searches run in the background — stack multiple searches to build your list. Results deduplicate automatically.
                  </p>
                </div>
              </TabsContent>

              {/* AI Search */}
              <TabsContent value="ai" className="mt-0 space-y-3">
                <div className="bg-card border rounded-lg p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h2 className="text-sm font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        AI-Powered Discovery
                      </h2>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Describe your ideal prospect. We find, verify, and source contact info automatically.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold tabular-nums">
                        {(parseInt(aiResultCap, 10) || 25) * (aiModel === "claude-sonnet-4-5" ? 5 : 2)} credits est.
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {parseInt(aiResultCap, 10) || 25} results × {aiModel === "claude-sonnet-4-5" ? 5 : 2} cr/lead
                      </div>
                    </div>
                  </div>

                  {savedSearches && savedSearches.length > 0 && (
                    <div className="border rounded-md p-3 bg-muted/20 space-y-2">
                      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Saved Searches</Label>
                      <div className="flex flex-wrap gap-2">
                        {savedSearches.map((ss) => (
                          <div key={ss.id} className="flex items-center gap-1 border rounded-md bg-card px-2 py-1" data-testid={`saved-search-${ss.id}`}>
                            <span className="text-xs font-medium">{ss.name}</span>
                            {ss.recurring && <Badge variant="secondary" className="text-[9px] h-4 px-1">monthly</Badge>}
                            <button
                              onClick={() => runSavedMutation.mutate(ss.id)}
                              disabled={runSavedMutation.isPending}
                              className="ml-1 text-primary hover:opacity-80 disabled:opacity-40"
                              title="Run now"
                              data-testid={`button-run-saved-${ss.id}`}
                            >
                              {runSavedMutation.isPending && runSavedMutation.variables === ss.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Play className="w-3 h-3" />}
                            </button>
                            <button
                              onClick={() => { if (confirm(`Delete saved search "${ss.name}"?`)) deleteSavedMutation.mutate(ss.id); }}
                              className="text-muted-foreground hover:text-destructive"
                              title="Delete"
                              data-testid={`button-delete-saved-${ss.id}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAiModel("claude-haiku-4-5")}
                      className={`rounded-lg border p-3 text-left transition-colors ${aiModel === "claude-haiku-4-5" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                      data-testid="button-scout"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Scout</div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span onClick={e => e.stopPropagation()} className="text-muted-foreground hover:text-foreground cursor-help">
                              <HelpCircle className="w-3.5 h-3.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[240px] text-xs p-3 space-y-1.5">
                            <p className="font-semibold text-foreground">Scout — Fast Discovery</p>
                            <p>AI generates a candidate list, then Google Places verifies each business.</p>
                            <p className="text-muted-foreground">Delivers: name, address, phone, website, and rating. No email or contact person.</p>
                            <p className="font-medium">2 cr/lead · free if no candidates found</p>
                            <p className="text-muted-foreground italic">Tip: cast a wide net with Scout, then Enrich only the winners — 4 cr total vs 5 cr for Analyst with email.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">Fast, broad search · 2 cr/lead</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiModel("claude-sonnet-4-5")}
                      className={`rounded-lg border p-3 text-left transition-colors ${aiModel === "claude-sonnet-4-5" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                      data-testid="button-analyst"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Analyst</div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span onClick={e => e.stopPropagation()} className="text-muted-foreground hover:text-foreground cursor-help">
                              <HelpCircle className="w-3.5 h-3.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[260px] text-xs p-3 space-y-1.5">
                            <p className="font-semibold text-foreground">Analyst — Deep Enrichment</p>
                            <p>7-phase pipeline: AI discovery → Apollo org lookup → marketplace &amp; web search → Hunter email cascade → NeverBounce verification → Google Places confirmation.</p>
                            <p className="text-muted-foreground">Delivers: everything Scout gives + email address, contact name &amp; role.</p>
                            <p className="font-medium">5 cr/lead with email · 2 cr/lead without email</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">7-phase · deep enrichment · 5 cr/lead with email</div>
                    </button>
                  </div>

                  {/* Deep Analyst — premium Opus layer (Analyst path only) */}
                  {aiModel === "claude-sonnet-4-5" && (
                    <div className={`rounded-lg border p-3 space-y-3 ${deepAnalyst ? "border-violet-300 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-900/10" : "border-border"}`}>
                      <label className="flex items-center justify-between gap-3 cursor-pointer">
                        <span className="flex items-start gap-2">
                          <Sparkles className={`w-4 h-4 mt-0.5 shrink-0 ${deepAnalyst ? "text-violet-600" : "text-muted-foreground"}`} />
                          <span>
                            <span className="text-sm font-medium flex items-center gap-1.5">
                              Deep Analyst
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-900/40 rounded px-1.5 py-0.5">Premium</span>
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">
                              Opus market mapping · 0-100 scoring · booker-hunt escalation (multiple contacts) · final logic audit
                            </span>
                          </span>
                        </span>
                        <Switch checked={deepAnalyst} onCheckedChange={setDeepAnalyst} data-testid="switch-deep-analyst" />
                      </label>
                      {deepAnalyst && (
                        <div className="pt-1 border-t border-violet-200/60 dark:border-violet-900/40 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-foreground">Acceptance score</label>
                            <span className="text-xs tabular-nums text-violet-700 dark:text-violet-300 font-semibold" data-testid="text-score-threshold">{scoreThreshold}+ / 100</span>
                          </div>
                          <input
                            type="range" min={0} max={100} step={5}
                            value={scoreThreshold}
                            onChange={(e) => setScoreThreshold(parseInt(e.target.value, 10))}
                            className="w-full accent-violet-600"
                            data-testid="slider-score-threshold"
                          />
                          <p className="text-[10px] text-muted-foreground">
                            Only records Opus scores at or above this bar are kept. Higher = fewer, better-qualified leads. Conservative default.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Analyst phase progress */}
                  {analystRunning && (
                    <div className="bg-muted/30 border rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
                        <span className="text-sm font-medium">{analystPhaseLabel}</span>
                      </div>
                      {analystPhaseDetail && (
                        <p className="text-xs text-muted-foreground pl-6">{analystPhaseDetail}</p>
                      )}
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div
                          className={`rounded-full h-1.5 transition-all duration-700 ${deepAnalyst ? "bg-violet-600" : "bg-primary"}`}
                          style={{ width: `${Math.min(100, (analystPhase / (deepAnalyst ? 8 : 7)) * 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground">
                        <span>Phase {analystPhase} of {deepAnalyst ? 8 : 7}</span>
                        <span>{Math.round(Math.min(100, (analystPhase / (deepAnalyst ? 8 : 7)) * 100))}%</span>
                      </div>
                    </div>
                  )}

                  {/* Analyst run summary — true cost-per-lead */}
                  {!analystRunning && analystDone && (
                    <div className="bg-emerald-50/60 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-900/40 rounded-lg p-4 space-y-2" data-testid="panel-analyst-summary">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span className="text-sm font-medium">
                          Added {analystDone.inserted} leads
                          {typeof analystDone.emailLeadCount === "number" ? ` · ${analystDone.emailLeadCount} with verified email` : ""}
                          {typeof analystDone.reachableCount === "number" ? ` · ${analystDone.reachableCount} reachable` : ""}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <div className="text-muted-foreground">Credits used</div>
                          <div className="font-semibold tabular-nums" data-testid="text-analyst-credits">{analystDone.creditsCharged ?? 0}</div>
                        </div>
                        {typeof analystDone.trueCostUsd === "number" && (
                          <div>
                            <div className="text-muted-foreground">True cost</div>
                            <div className="font-semibold tabular-nums" data-testid="text-analyst-true-cost">${analystDone.trueCostUsd.toFixed(2)}</div>
                          </div>
                        )}
                        {analystDone.avgCostPerLeadUsd != null && (
                          <div>
                            <div className="text-muted-foreground">Cost / lead</div>
                            <div className="font-semibold tabular-nums" data-testid="text-analyst-cpl">${Number(analystDone.avgCostPerLeadUsd).toFixed(3)}</div>
                          </div>
                        )}
                        {analystDone.avgCostPerEmailLeadUsd != null && (
                          <div>
                            <div className="text-muted-foreground">Cost / email lead</div>
                            <div className="font-semibold tabular-nums" data-testid="text-analyst-cpel">${Number(analystDone.avgCostPerEmailLeadUsd).toFixed(3)}</div>
                          </div>
                        )}
                      </div>
                      {analystDone.sourceStats && Object.keys(analystDone.sourceStats).length > 0 && (
                        <div className="pt-1" data-testid="panel-analyst-source-stats">
                          <div className="text-[11px] text-muted-foreground mb-1">Contacts found by source</div>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(analystDone.sourceStats as Record<string, { attempts: number; hits: number }>)
                              .sort((a, b) => (b[1]?.hits ?? 0) - (a[1]?.hits ?? 0))
                              .map(([src, st]) => (
                                <span
                                  key={src}
                                  className="inline-flex items-center gap-1 rounded border border-border bg-background/60 px-1.5 py-0.5 text-[10px] tabular-nums"
                                  data-testid={`stat-source-${src}`}
                                >
                                  <span className="font-medium">{src}</span>
                                  <span className="text-muted-foreground">{st.hits}/{st.attempts}</span>
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                      {analystDone.channelStats && Object.keys(analystDone.channelStats).length > 0 && (
                        <div className="pt-1" data-testid="panel-analyst-channel-stats">
                          <div className="text-[11px] text-muted-foreground mb-1">Non-email channels captured</div>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(analystDone.channelStats as Record<string, number>)
                              .sort((a, b) => b[1] - a[1])
                              .map(([kind, n]) => (
                                <span
                                  key={kind}
                                  className="inline-flex items-center gap-1 rounded border border-border bg-background/60 px-1.5 py-0.5 text-[10px] tabular-nums"
                                  data-testid={`stat-channel-${kind}`}
                                >
                                  <span className="font-medium">{kind}</span>
                                  <span className="text-muted-foreground">{n}</span>
                                </span>
                              ))}
                          </div>
                        </div>
                      )}
                      {analystDone.stoppedBy && (
                        <p className="text-[11px] text-muted-foreground">
                          {analystDone.stoppedBy === "time" ? "Stopped at the ~10-minute time budget." : "Stopped at the spend cap."}
                        </p>
                      )}
                      {Array.isArray(analystDone.skippedProviders) && analystDone.skippedProviders.length > 0 && (
                        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 px-3 py-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                          <div className="text-[11px] text-amber-800 dark:text-amber-300 space-y-0.5">
                            <p className="font-medium">Boost your email hit rate — {analystDone.skippedProviders.length} provider{analystDone.skippedProviders.length > 1 ? "s" : ""} not configured</p>
                            <p>
                              {(analystDone.skippedProviders as string[]).map((p: string) => {
                                if (p === "hunter") return "Hunter.io domain search (HUNTER_API_KEY) — best for venues; free tier: 25 searches/mo";
                                if (p === "brave_search") return "Brave Search (BRAVE_SEARCH_API_KEY) — web-query discovery for off-grid performers; ~$3/1k queries";
                                return p;
                              }).join(" · ")}
                            </p>
                            <p className="text-amber-700/80 dark:text-amber-400/80">Add these in the Replit Secrets panel to unlock all email-finding steps.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <VoiceToTextTextarea
                    id="ai-brief"
                    placeholder='e.g. "Mid-size country clubs in Texas that book regular live music for member events. Capacity 100–300. Avoid wedding-only venues."'
                    value={aiBrief}
                    onChange={(e) => setAiBrief(e.target.value)}
                    rows={3}
                    className="text-sm resize-none"
                    data-testid="textarea-ai-brief"
                  />

                  {/* Result Cap + Spend Cap — just below prompt */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="ai-cap" className="text-xs text-muted-foreground whitespace-nowrap">Result Cap</Label>
                      <Input
                        id="ai-cap"
                        type="number"
                        min="1"
                        max="500"
                        placeholder="None"
                        value={aiResultCap}
                        onChange={(e) => setAiResultCap(e.target.value)}
                        className="w-28 h-8 text-sm"
                        data-testid="input-ai-cap"
                      />
                    </div>
                    {aiModel === "claude-sonnet-4-5" && (
                      <div className="flex items-center gap-2">
                        <Label htmlFor="ai-spend-cap" className="text-xs text-muted-foreground whitespace-nowrap">Spend Cap ($)</Label>
                        <Input
                          id="ai-spend-cap"
                          type="number"
                          min="0"
                          step="0.50"
                          placeholder="None"
                          value={aiSpendCap}
                          onChange={(e) => setAiSpendCap(e.target.value)}
                          className="w-28 h-8 text-sm"
                          data-testid="input-ai-spend-cap"
                        />
                        <span className="text-[10px] text-muted-foreground">stops at ~10 min</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={() => setShowRefinements((v) => !v)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors select-none"
                      data-testid="button-toggle-refinements"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showRefinements ? "" : "-rotate-90"}`} />
                      {showRefinements ? "Hide refinements" : "Refine search (optional)"}
                    </button>

                    {showRefinements && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="ai-scope-kind" className="text-xs">Geographic Scope</Label>
                            <Select value={aiScopeKind} onValueChange={(v) => setAiScopeKind(v as any)}>
                              <SelectTrigger id="ai-scope-kind" data-testid="select-ai-scope-kind">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="city">Single City</SelectItem>
                                <SelectItem value="state">State</SelectItem>
                                <SelectItem value="region">Region (NE/SE/MW/SW/W)</SelectItem>
                                <SelectItem value="cities">Multiple Cities</SelectItem>
                                <SelectItem value="national">National</SelectItem>
                                <SelectItem value="custom">Custom Prompt</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-scope-value" className="text-xs">
                              {aiScopeKind === "national" ? "(no value needed)" : aiScopeKind === "cities" ? "Cities (comma-separated)" : "Value"}
                            </Label>
                            <Input
                              id="ai-scope-value"
                              placeholder={
                                aiScopeKind === "city" ? "Austin, TX" :
                                aiScopeKind === "state" ? "Texas" :
                                aiScopeKind === "region" ? "Southeast" :
                                aiScopeKind === "cities" ? "Austin, Dallas, Houston" :
                                aiScopeKind === "custom" ? "e.g. Gulf Coast resort towns" : ""
                              }
                              value={aiScopeValue}
                              onChange={(e) => setAiScopeValue(e.target.value)}
                              disabled={aiScopeKind === "national"}
                              data-testid="input-ai-scope-value"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="ai-venue-types" className="text-xs">Business Types</Label>
                            <Input id="ai-venue-types" placeholder="law firm, magician, real estate agency" value={aiVenueTypesText} onChange={(e) => setAiVenueTypesText(e.target.value)} data-testid="input-ai-venue-types" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-cap-min" className="text-xs">Capacity Min</Label>
                            <Input id="ai-cap-min" type="number" placeholder="100" value={aiCapacityMin} onChange={(e) => setAiCapacityMin(e.target.value)} data-testid="input-ai-cap-min" />
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="ai-cap-max" className="text-xs">Capacity Max</Label>
                            <Input id="ai-cap-max" type="number" placeholder="300" value={aiCapacityMax} onChange={(e) => setAiCapacityMax(e.target.value)} data-testid="input-ai-cap-max" />
                          </div>
                        </div>

                        {/* Target contact type — passed to Apollo enrich as person title filter */}
                        <div className="space-y-1">
                          <Label htmlFor="ai-target-contact" className="text-xs">Target contact type</Label>
                          <Input
                            id="ai-target-contact"
                            placeholder='e.g. "talent buyer", "CEO", "marketing director"'
                            value={aiTargetContact}
                            onChange={(e) => setAiTargetContact(e.target.value)}
                            data-testid="input-ai-target-contact"
                          />
                        </div>

                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-2 border-t">
                    <Button onClick={handleAiSearch} disabled={!aiBrief.trim() || aiSearchMutation.isPending || analystRunning} data-testid="button-ai-search">
                      {(aiSearchMutation.isPending || analystRunning)
                        ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />{analystRunning ? "Analyst running…" : "Searching…"}</>
                        : <><Sparkles className="w-4 h-4 mr-1.5" />Run AI Search</>}
                    </Button>
                  </div>
                </div>

                {aiSearchMutation.isSuccess && (
                  <div className="bg-card border rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium text-muted-foreground">Save this search?</span>
                    <Input
                      placeholder="Give it a name…"
                      value={aiSaveAs}
                      onChange={(e) => setAiSaveAs(e.target.value)}
                      className="max-w-[200px] h-8 text-xs"
                      data-testid="input-ai-save-as"
                    />
                    <div className="flex items-center gap-1.5">
                      <Checkbox
                        id="ai-recurring-post"
                        checked={aiRecurring}
                        onCheckedChange={(v) => setAiRecurring(!!v)}
                        data-testid="checkbox-ai-recurring"
                      />
                      <Label htmlFor="ai-recurring-post" className="text-xs cursor-pointer">Repeat monthly</Label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveSearch}
                      disabled={!aiSaveAs.trim() || saveSearchMutation.isPending}
                      data-testid="button-ai-save-search"
                    >
                      {saveSearchMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span className="ml-1.5">Save</span>
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Active searches progress */}
            {searches.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Active Searches</h3>
                {searches.map((search) => {
                  const isRunning = search.status === "RUNNING" || search.status === "PENDING";
                  const pct = search.totalSubSearches > 0
                    ? Math.round((search.completedSubSearches / search.totalSubSearches) * 100)
                    : isRunning ? 0 : 100;
                  return (
                    <div key={search.id} className="bg-card border rounded-lg px-4 py-3">
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {isRunning && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />}
                          {search.status === "COMPLETE" && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
                          {search.status === "FAILED" && <X className="w-3.5 h-3.5 text-destructive shrink-0" />}
                          <span className="text-sm font-medium truncate">
                            "{search.keyword}" near {search.location}
                          </span>
                          {search.source === "ai" ? (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 shrink-0 bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-300 border-0" data-testid={`badge-source-${search.id}`}>
                              AI Analyst
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 shrink-0" data-testid={`badge-source-${search.id}`}>
                              Grid Search
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 text-xs text-muted-foreground">
                          {search.status === "COMPLETE" && (
                            <span className="text-green-600 dark:text-green-400 font-medium">+{search.resultCount} added</span>
                          )}
                          {search.status === "FAILED" && (
                            <span className="text-destructive">{search.errorMessage || "Failed"}</span>
                          )}
                          {isRunning && search.totalSubSearches > 0 && (
                            <span>{search.completedSubSearches}/{search.totalSubSearches} zones</span>
                          )}
                        </div>
                      </div>
                      {isRunning && (
                        <Progress value={pct} className="h-1.5" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sandbox table */}
            {sandboxLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-base font-medium text-muted-foreground">No prospects in staging yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Run a search above to start building your prospect list.
                </p>
              </div>
            ) : (
              <div className="bg-card border rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    Staging Area
                    <Badge variant="secondary">{items.length}</Badge>
                  </h3>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">
                      {activeItems.length} active · {items.filter((i) => i.isDuplicate).length} potential duplicates
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1.5"
                      onClick={() => bulkEnrichMutation.mutate()}
                      disabled={bulkEnrichMutation.isPending || activeItems.every((i) => i.aiContactEmail || !i.website)}
                      data-testid="button-bulk-reenrich"
                    >
                      {bulkEnrichMutation.isPending
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <RefreshCw className="w-3.5 h-3.5" />}
                      Re-enrich missing emails
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="w-10 px-3 py-2.5 text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground text-xs">Skip</span>
                            </TooltipTrigger>
                            <TooltipContent>Toggle to exclude from import</TooltipContent>
                          </Tooltip>
                        </th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Business</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Location</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Contact</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Email</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Phone</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Website</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Rating</th>
                        <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Maps</th>
                        <th className="px-3 py-2.5 text-right font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {items.map((item) => (
                        <PlatformSandboxRow
                          key={item.id}
                          item={item}
                          isSkipped={skipIds.has(item.id)}
                          busyKind={rowBusy[item.id] ?? null}
                          onToggleSkip={() => toggleSkip(item.id)}
                          onRemove={() => removeMutation.mutate(item.id)}
                          onEnrich={() => handleEnrich(item)}
                          onFindEmail={() => handleFindEmail(item)}
                          onApollo={() => handleApollo(item)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Clear Confirm */}
        <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear sandbox?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes all {items.length} staging prospects and their search history. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => clearMutation.mutate()}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="button-confirm-clear"
              >
                {clearMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Clear sandbox
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Import Dialog */}
        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="max-w-2xl" data-testid="dialog-import-prospects">

            {importStep === "form" && (
              <>
                <DialogHeader>
                  <DialogTitle>Import to Platform Prospects</DialogTitle>
                  <DialogDescription>
                    {activeItems.length} staging prospect{activeItems.length !== 1 ? "s" : ""} will be imported to the Platform Prospects table.
                    You'll need {activeItems.length} credit{activeItems.length !== 1 ? "s" : ""} (you have {credits?.totalBalance ?? 0}).
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {credits && credits.totalBalance < activeItems.length && (
                    <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800 dark:text-amber-300">
                        You need {activeItems.length} credits but only have {credits.totalBalance}. Add more credits before importing, or reduce the import size by skipping items.
                      </p>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Click "Next" to check for duplicates before confirming the import.
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleCheckDuplicates}
                    disabled={activeItems.length === 0 || (credits !== undefined && credits.totalBalance < activeItems.length)}
                    data-testid="button-import-next"
                  >
                    Next →
                  </Button>
                </DialogFooter>
              </>
            )}

            {importStep === "checking" && (
              <>
                <DialogHeader>
                  <DialogTitle>Checking for Duplicates…</DialogTitle>
                  <DialogDescription>Comparing against your existing Platform Prospects.</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-10">
                  <Loader2 className="w-10 h-10 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">This may take a moment for large lists.</p>
                </div>
              </>
            )}

            {importStep === "review" && (
              <>
                <DialogHeader>
                  {duplicateMatches.length === 0 ? (
                    <>
                      <DialogTitle className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                        No Duplicates Found
                      </DialogTitle>
                      <DialogDescription>
                        All {totalToImport} prospect{totalToImport !== 1 ? "s" : ""} are unique and ready to import. The sandbox will be cleared after import.
                      </DialogDescription>
                    </>
                  ) : (
                    <>
                      <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        {duplicateMatches.length} Potential Duplicate{duplicateMatches.length !== 1 ? "s" : ""} Found
                      </DialogTitle>
                      <DialogDescription>
                        These prospects may already exist. Uncheck any you want to skip — duplicates are excluded by default.
                      </DialogDescription>
                    </>
                  )}
                </DialogHeader>

                {duplicateMatches.length > 0 && (
                  <>
                    <div className="flex items-center justify-between py-1">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">{reviewImportCount}</span> of <span className="font-medium text-foreground">{totalToImport}</span> prospects will be imported
                      </p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setDupSkipIds(new Set(duplicateMatches.map((d) => d.sandboxItemId)))} data-testid="button-dup-skip-all">
                          Skip All Duplicates
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setDupSkipIds(new Set())} data-testid="button-dup-import-all">
                          Import All
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/60 text-muted-foreground">
                          <tr>
                            <th className="w-10 px-3 py-2 text-center font-medium">Import</th>
                            <th className="px-3 py-2 text-left font-medium">Business Name</th>
                            <th className="px-3 py-2 text-left font-medium">Matched On</th>
                            <th className="px-3 py-2 text-left font-medium">Existing Record</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {duplicateMatches.map((dup) => {
                            const willImport = !dupSkipIds.has(dup.sandboxItemId);
                            return (
                              <tr key={dup.sandboxItemId} className={`transition-colors ${willImport ? "bg-background" : "bg-muted/30"}`}>
                                <td className="px-3 py-2.5 text-center">
                                  <input type="checkbox" checked={willImport} onChange={() => toggleDupSkip(dup.sandboxItemId)}
                                    className="h-4 w-4 rounded border-gray-300 accent-primary cursor-pointer"
                                    data-testid={`checkbox-dup-import-${dup.sandboxItemId}`} />
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="font-medium">{dup.businessName}</div>
                                  {dup.phone && <div className="text-xs text-muted-foreground">{dup.phone}</div>}
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="flex flex-wrap gap-1">
                                    {dup.matchedFields.map((f) => (
                                      <span key={f} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                        {f.charAt(0).toUpperCase() + f.slice(1)}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-3 py-2.5">
                                  <div className="font-medium">{dup.existingRecord.name}</div>
                                  <div className="text-xs text-muted-foreground capitalize">{dup.existingRecord.type}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                  <Button variant="outline" onClick={() => setImportStep("form")} className="sm:mr-auto" data-testid="button-dup-back">
                    ← Back
                  </Button>
                  <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleReviewImport}
                    disabled={importMutation.isPending || reviewImportCount === 0}
                    data-testid="button-dup-confirm-import"
                  >
                    {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
                    Import {reviewImportCount} Prospect{reviewImportCount !== 1 ? "s" : ""}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
    </PlatformLayout>
  );
}

// ── Sandbox Row ───────────────────────────────────────────────────────────────

interface PlatformSandboxRowProps {
  item: PlatformSandboxItem;
  isSkipped: boolean;
  busyKind: "enrich" | "email" | "apollo" | null;
  onToggleSkip: () => void;
  onRemove: () => void;
  onEnrich: () => void;
  onFindEmail: () => void;
  onApollo: () => void;
}

function DeliverabilityBadge({ status, source, confidence, itemId }: { status?: string | null; source?: string | null; confidence?: string | null; itemId: string }) {
  if (!status || status === "none") return null;
  const s = status.toLowerCase();
  let cfg: { label: string; cls: string; Icon: typeof Check };
  // MX-only (low confidence): domain accepts mail but the mailbox is unproven.
  if (confidence === "low") {
    cfg = { label: "MX-only", cls: "text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20", Icon: HelpCircle };
  } else if (s === "valid") {
    cfg = { label: "Valid", cls: "text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20", Icon: CheckCircle2 };
  } else if (s === "catchall" || s === "accept_all" || s === "accept-all") {
    cfg = { label: "Catch-all", cls: "text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20", Icon: AlertTriangle };
  } else if (s === "invalid" || s === "disposable") {
    cfg = { label: s === "disposable" ? "Disposable" : "Invalid", cls: "text-red-700 dark:text-red-400 border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20", Icon: Ban };
  } else {
    cfg = { label: "Unknown", cls: "text-muted-foreground border-border bg-muted/40", Icon: HelpCircle };
  }
  const { Icon } = cfg;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-0.5 rounded border px-1 py-0 h-4 text-[10px] leading-none shrink-0 ${cfg.cls}`}
          data-testid={`badge-deliverability-${itemId}`}
        >
          <Icon className="w-2.5 h-2.5" />{cfg.label}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Deliverability: {cfg.label}{source ? ` · found via ${source}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

type ContactChannel = { kind: "phone" | "instagram" | "contact_form" | "links" | "marketplace"; value: string; source?: string };

const CHANNEL_META: Record<ContactChannel["kind"], { Icon: typeof Check; label: string; href: (v: string) => string }> = {
  phone: { Icon: Phone, label: "Phone", href: (v) => `tel:${v}` },
  instagram: { Icon: Instagram, label: "Instagram", href: (v) => (v.startsWith("http") ? v : `https://instagram.com/${v.replace(/^@/, "")}`) },
  contact_form: { Icon: FileText, label: "Contact form", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
  links: { Icon: Link2, label: "Links", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
  marketplace: { Icon: Store, label: "Marketplace", href: (v) => (v.startsWith("http") ? v : `https://${v}`) },
};

function ContactChannels({ channels, itemId }: { channels?: ContactChannel[] | null; itemId: string }) {
  // Phone is already shown in its own column — surface the other reach paths here.
  const list = (channels || []).filter((c) => c && c.kind !== "phone" && c.value);
  if (list.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`channels-${itemId}`}>
      {list.map((c, i) => {
        const baseMeta = CHANNEL_META[c.kind];
        if (!baseMeta) return null;
        // Surface LinkedIn links with their own icon + label instead of the generic "Links".
        const isLinkedIn = c.kind === "links" && /linkedin\.com/i.test(c.value);
        const meta = isLinkedIn ? { ...baseMeta, Icon: Linkedin, label: "LinkedIn" } : baseMeta;
        const { Icon } = meta;
        return (
          <Tooltip key={`${c.kind}-${i}`}>
            <TooltipTrigger asChild>
              <a
                href={meta.href(c.value)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 rounded border border-border bg-muted/40 px-1 py-0 h-4 text-[10px] leading-none text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`channel-${c.kind}-${itemId}`}
              >
                <Icon className="w-2.5 h-2.5" />{meta.label}
              </a>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] break-all">
              {meta.label}: {c.value}{c.source ? ` · via ${c.source}` : ""}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

function ContactReason({ reason, itemId }: { reason?: string | null; itemId: string }) {
  if (!reason) return <span className="text-muted-foreground/40">—</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground/70 cursor-help"
          data-testid={`reason-no-email-${itemId}`}
        >
          <HelpCircle className="w-3 h-3" /> No email
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px]">{reason}</TooltipContent>
    </Tooltip>
  );
}

// Deep Analyst: additional booker contacts surfaced beyond the primary. Reads
// either bookerContacts (escalation ladder) or additionalContacts (cascade).
function ExtraContacts({ item }: { item: PlatformSandboxItem }) {
  const cj = item.aiConfidenceJson;
  const extras = (cj?.bookerContacts && cj.bookerContacts.length ? cj.bookerContacts : cj?.additionalContacts) || [];
  // Don't count the primary (already shown above) toward the "+N more" count.
  const more = extras.filter((c) => {
    const email = (c?.email || "").toLowerCase();
    const primaryEmail = (item.aiContactEmail || "").toLowerCase();
    if (email && primaryEmail && email === primaryEmail) return false;
    if ((c as any)?.isPrimary) return false;
    return Boolean(c?.name || c?.email);
  });
  if (more.length === 0 && !cj?.bookerEscalated) return null;
  return (
    <div className="flex items-center gap-1 mt-0.5">
      {more.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-300 cursor-help"
              data-testid={`extra-contacts-${item.id}`}
            >
              <Users className="w-3 h-3" />+{more.length} contact{more.length > 1 ? "s" : ""}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] text-xs space-y-1">
            {more.map((c, i) => (
              <div key={i} className="leading-tight">
                <span className="font-semibold">{c.name || "Unknown"}</span>
                {c.title ? <span className="text-muted-foreground"> — {c.title}</span> : null}
                {c.email ? <div className="text-muted-foreground">{c.email}</div> : null}
              </div>
            ))}
          </TooltipContent>
        </Tooltip>
      )}
      {cj?.bookerEscalated && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 cursor-help"
              data-testid={`escalated-${item.id}`}
            >
              <ChevronDown className="w-3 h-3 rotate-180" />Escalated
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[260px] text-xs">
            {cj.bookerReason || "Booker hunt escalated up the org chart to find a decision-maker."}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

function PlatformSandboxRow({ item, isSkipped, busyKind, onToggleSkip, onRemove, onEnrich, onFindEmail, onApollo }: PlatformSandboxRowProps) {
  const [showPhoto, setShowPhoto] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleMouseEnter() {
    if (item.photoReference && !photoError) {
      hoverTimeout.current = setTimeout(() => setShowPhoto(true), 300);
    }
  }
  function handleMouseLeave() {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setShowPhoto(false);
  }

  const photoUrl = item.photoReference
    ? `/api/lead-gen/photo?ref=${encodeURIComponent(item.photoReference)}&maxwidth=320`
    : null;

  return (
    <tr
      className={`group transition-colors ${
        isSkipped
          ? "opacity-40 bg-muted/30"
          : item.isDuplicate
          ? "bg-amber-50/50 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          : "hover:bg-muted/30"
      }`}
    >
      <td className="px-3 py-2.5 text-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onToggleSkip}
              className="w-4 h-4 rounded border border-muted-foreground/30 flex items-center justify-center hover:border-primary transition-colors"
              data-testid={`checkbox-skip-${item.id}`}
            >
              {isSkipped && <X className="w-3 h-3 text-muted-foreground" />}
            </button>
          </TooltipTrigger>
          <TooltipContent>{isSkipped ? "Include in import" : "Exclude from import"}</TooltipContent>
        </Tooltip>
      </td>

      <td className="px-4 py-2.5">
        <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium leading-tight">{item.businessName}</span>
            {item.quadrant && (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0 h-4 leading-none shrink-0" data-testid={`badge-quadrant-${item.id}`}>
                {item.quadrant}
              </Badge>
            )}
            {item.isDuplicate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                </TooltipTrigger>
                <TooltipContent>Matches an existing platform prospect</TooltipContent>
              </Tooltip>
            )}
            {item.isAiGenerated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="text-[10px] h-4 px-1 leading-none shrink-0 gap-0.5" data-testid={`badge-ai-${item.id}`}>
                    <Sparkles className="w-2.5 h-2.5" />AI
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{item.aiSourceLabel || "AI Discovery"}{item.aiNotes ? ` — ${item.aiNotes}` : ""}</TooltipContent>
              </Tooltip>
            )}
            {typeof item.aiFitScore === "number" && (
              <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`fit-${item.id}`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-3 h-3 ${n <= 6 - (item.aiFitScore || 5) ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/30"}`} />
                ))}
              </span>
            )}
            {typeof item.aiConfidenceJson?.score === "number" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`inline-flex items-center shrink-0 text-[10px] font-semibold rounded px-1 h-4 leading-none cursor-help ${
                      (item.aiConfidenceJson.score ?? 0) >= 80
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : (item.aiConfidenceJson.score ?? 0) >= 60
                        ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    }`}
                    data-testid={`score-${item.id}`}
                  >
                    {item.aiConfidenceJson.score}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-xs">
                  <span className="font-semibold">Opus qualification: {item.aiConfidenceJson.score}/100</span>
                  {item.aiConfidenceJson.scoreReason ? <><br />{item.aiConfidenceJson.scoreReason}</> : null}
                </TooltipContent>
              </Tooltip>
            )}
            {item.aiConfidenceJson?.audit && item.aiConfidenceJson.audit.verdict && item.aiConfidenceJson.audit.verdict !== "pass" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold rounded px-1 h-4 leading-none cursor-help bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                    data-testid={`audit-${item.id}`}
                  >
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {item.aiConfidenceJson.audit.verdict === "mismatch" ? "Mismatch" : "Flag"}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-[260px] text-xs">
                  <span className="font-semibold">Audit: {item.aiConfidenceJson.audit.verdict}</span>
                  {item.aiConfidenceJson.audit.reason ? <><br />{item.aiConfidenceJson.audit.reason}</> : null}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {showPhoto && photoUrl && !photoError && (
            <div className="absolute left-0 top-full z-50 mt-1 shadow-xl rounded-lg overflow-hidden border bg-card w-64">
              <img src={photoUrl} alt={item.businessName} className="w-full object-cover"
                onError={() => { setPhotoError(true); setShowPhoto(false); }} />
              <div className="px-3 py-2 text-xs text-muted-foreground truncate">{item.businessName}</div>
            </div>
          )}
        </div>
      </td>

      <td className="px-4 py-2.5 text-muted-foreground">
        <div className="flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate max-w-[180px]">
            {[item.city, item.state].filter(Boolean).join(", ") || item.address || "—"}
          </span>
        </div>
      </td>

      <td className="px-4 py-2.5">
        {item.aiContactName ? (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-medium leading-tight truncate max-w-[150px]" data-testid={`contact-name-${item.id}`}>{item.aiContactName}</span>
            {item.aiContactRole && <span className="text-[11px] text-muted-foreground truncate max-w-[150px]">{item.aiContactRole}</span>}
            <ExtraContacts item={item} />
          </div>
        ) : (
          <span className="text-muted-foreground/40">—</span>
        )}
      </td>

      <td className="px-4 py-2.5">
        <div className="flex flex-col gap-1 min-w-0">
          {item.aiContactEmail ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <a
                href={`mailto:${item.aiContactEmail}`}
                className="truncate max-w-[170px] hover:text-foreground transition-colors text-sm"
                title={item.aiContactEmail}
                data-testid={`email-${item.id}`}
              >
                {item.aiContactEmail}
              </a>
              <DeliverabilityBadge
                status={item.aiConfidenceJson?.emailStatus}
                source={item.aiConfidenceJson?.emailSource}
                confidence={item.aiConfidenceJson?.emailConfidence}
                itemId={item.id}
              />
            </div>
          ) : (
            <ContactReason reason={item.aiConfidenceJson?.reason} itemId={item.id} />
          )}
          <ContactChannels channels={item.aiConfidenceJson?.channels} itemId={item.id} />
        </div>
      </td>

      <td className="px-4 py-2.5">
        {item.phone ? (
          <div className="flex items-center gap-1 text-muted-foreground">
            <Phone className="w-3.5 h-3.5 shrink-0" />
            <a href={`tel:${item.phone}`} className="hover:text-foreground transition-colors">{item.phone}</a>
          </div>
        ) : <span className="text-muted-foreground/40">—</span>}
      </td>

      <td className="px-4 py-2.5">
        {item.website ? (
          <a
            href={item.website.startsWith("http") ? item.website : `https://${item.website}`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary hover:underline truncate max-w-[160px]"
            data-testid={`link-website-${item.id}`}
          >
            <Globe className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{item.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
          </a>
        ) : <span className="text-muted-foreground/40">—</span>}
      </td>

      <td className="px-4 py-2.5">
        {item.googleRating ? (
          <div className="flex items-center gap-1 text-sm">
            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            <span className="font-medium">{item.googleRating.toFixed(1)}</span>
            {item.googleRatingsCount && <span className="text-muted-foreground">({item.googleRatingsCount.toLocaleString()})</span>}
          </div>
        ) : <span className="text-muted-foreground/40">—</span>}
      </td>

      <td className="px-4 py-2.5">
        {item.googleMapsUrl ? (
          <a href={item.googleMapsUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
            data-testid={`link-maps-${item.id}`}>
            <ExternalLink className="w-3.5 h-3.5" />Maps
          </a>
        ) : <span className="text-muted-foreground/40">—</span>}
      </td>

      <td className="px-3 py-2.5 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5 justify-end">
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={onEnrich} disabled={busyKind !== null}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                data-testid={`button-enrich-${item.id}`}>
                {busyKind === "enrich" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>Enrich with AI (1 credit)</TooltipContent>
          </Tooltip>
          {item.website && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onFindEmail} disabled={busyKind !== null}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                  data-testid={`button-find-email-${item.id}`}>
                  {busyKind === "email" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>Find contact email (1 credit)</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={onApollo} disabled={busyKind !== null}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary disabled:opacity-40"
                data-testid={`button-apollo-${item.id}`}>
                {busyKind === "apollo" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Linkedin className="w-3.5 h-3.5" />}
              </button>
            </TooltipTrigger>
            <TooltipContent>Apollo lookup (1 credit)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button onClick={onRemove}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                data-testid={`button-remove-${item.id}`}>
                <X className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Remove from staging</TooltipContent>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}
